import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Target, Compass, HeartHandshake, ShieldCheck, ArrowRight, Linkedin, Mail } from "lucide-react";
import { IMAGES, TRUST_VALUES } from "../data/site";
import { api } from "../lib/api";
import PageHero from "../components/common/PageHero";
import SectionHeading from "../components/common/SectionHeading";
import Reveal from "../components/common/Reveal";
import CTABand from "../components/common/CTABand";
import useSeo from "../hooks/useSeo";

const DEFAULT_VALUES = [
  { title: "Integrity", description: "We communicate honestly and make no claims we cannot support." },
  { title: "Partnership", description: "We treat clients, candidates and partners as long-term collaborators." },
  { title: "Precision", description: "Intelligent matching and human judgement, always over volume." },
  { title: "Transparency", description: "Clear terms, clear process and clear expectations at every step." },
];
const VALUE_ICONS = [ShieldCheck, HeartHandshake, Target, Compass];

export default function About() {
  useSeo({
    title: "About Us",
    description:
      "Learn about Azimi & Co. Vision Recruitment — a healthcare-first recruitment partner combining specialist expertise with AI Recruitment Intelligence, transparent partnerships and future-ready, scalable infrastructure.",
  });
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [team, setTeam] = useState([]);
  const [teamFailed, setTeamFailed] = useState(false);
  useEffect(() => {
    api.get("/site-content").then(({ data }) => { if (Array.isArray(data.core_values) && data.core_values.length) setValues(data.core_values); }).catch(() => {});
    let cancelled = false;
    const loadTeam = (attempt = 0) => {
      api.get("/team")
        .then(({ data }) => { if (!cancelled) { setTeam(Array.isArray(data) ? data : []); setTeamFailed(false); } })
        .catch(() => {
          if (cancelled) return;
          if (attempt < 2) setTimeout(() => loadTeam(attempt + 1), 800 * (attempt + 1));
          else setTeamFailed(true);
        });
    };
    loadTeam();
    return () => { cancelled = true; };
  }, []);
  return (
    <>
      <PageHero
        overline="About Us"
        title="A healthcare-first recruitment partner, built to scale"
        description="Azimi & Co. Vision Recruitment blends specialist healthcare expertise with AI Recruitment Intelligence — connecting organisations, professionals and partners through a transparent, relationship-led approach."
      />
      <section className="py-20 lg:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
          <Reveal>
            <div className="overflow-hidden rounded-2xl border border-border shadow-sm">
              <img src={IMAGES.partnership} alt="Recruitment partnership meeting" className="h-[420px] w-full object-cover" />
            </div>
          </Reveal>
          <Reveal delay={100}>
            <span className="overline text-xs text-gold">Who We Are</span>
            <h2 className="mt-4 font-serif text-3xl tracking-tight text-navy sm:text-4xl">Built once. Designed to scale.</h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-muted-foreground">
              <p>
                We are healthcare-first. Our foundation is clinical excellence — helping hospitals
                and care providers find the right professionals, supporting candidates through their
                careers, and collaborating with partners through split fee and international
                arrangements.
              </p>
              <p>
                Underpinning it all is our AI Recruitment Intelligence: technology that accelerates
                sourcing, screening and matching, with an experienced consultant approving every
                recommendation. It is the same infrastructure that lets us extend elite, structured
                talent solutions to organisations across every professional sector.
              </p>
            </div>
            <Link to="/why-choose-us" className="mt-8 inline-flex items-center gap-2 rounded-lg bg-navy px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-navy-light">
              Why Choose Us <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-20 lg:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <SectionHeading overline="Our Values" title="What guides our work" />
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {values.map((v, i) => {
              const Icon = VALUE_ICONS[i % VALUE_ICONS.length];
              return (
                <Reveal key={v.title} delay={(i % 4) * 70}>
                  <div className="h-full rounded-2xl border border-border bg-background p-7" data-testid={`value-card-${i}`}>
                    <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gold/10 text-gold">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h4 className="mt-5 font-serif text-lg text-navy">{v.title}</h4>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{v.description}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {(team.length > 0 || teamFailed) && (
        <section className="bg-background py-20 lg:py-24" data-testid="group-executive-section">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
            <SectionHeading overline="Group Executive" title="Leadership team" />
            {teamFailed && team.length === 0 && (
              <p className="mt-6 text-center text-sm text-muted-foreground" data-testid="team-load-error">
                Leadership profiles could not be loaded. Please refresh the page.
              </p>
            )}
            <div className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
              {team.map((m, i) => (
                <Reveal key={m.id} delay={(i % 3) * 80}>
                  <div className="group h-full overflow-hidden rounded-2xl border border-border bg-white" data-testid={`team-card-${m.id}`}>
                    <div className="aspect-[4/3] w-full overflow-hidden bg-navy/5">
                      {m.photo_url
                        ? <img src={m.photo_url} alt={m.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                        : <div className="flex h-full items-center justify-center font-serif text-4xl text-navy/20">{m.name?.[0] || "A"}</div>}
                    </div>
                    <div className="p-6">
                      <h4 className="font-serif text-lg text-navy">{m.name}</h4>
                      <p className="text-sm font-semibold text-gold-dark">{m.role}</p>
                      {m.bio && <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{m.bio}</p>}
                      <div className="mt-4 flex gap-2">
                        {m.linkedin && <a href={m.linkedin} target="_blank" rel="noreferrer" aria-label={`${m.name} on LinkedIn`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-navy hover:border-gold hover:text-gold"><Linkedin className="h-4 w-4" /></a>}
                        {m.email && <a href={`mailto:${m.email}`} aria-label={`Email ${m.name}`} className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-border text-navy hover:border-gold hover:text-gold"><Mail className="h-4 w-4" /></a>}
                      </div>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-16">
        <div className="mx-auto flex max-w-5xl flex-wrap items-center justify-center gap-x-12 gap-y-6 px-6">
          {TRUST_VALUES.map(({ icon: Icon, label }) => (
            <div key={label} className="flex items-center gap-3 text-navy">
              <Icon className="h-5 w-5 text-gold" />
              <span className="text-sm font-semibold">{label}</span>
            </div>
          ))}
        </div>
      </section>

      <CTABand
        title="Ready to build a lasting recruitment partnership?"
        description="Get to know the team behind Azimi & Co. Vision Recruitment and discover how our approach can support your goals."
        primary={{ label: "Start a Conversation", to: "/contact" }}
        secondary={{ label: "Our Expertise", to: "/why-choose-us" }}
      />
    </>
  );
}
